/* eslint-disable max-lines */
import type { IConversation } from '~/entities/container/conversation/interfaces/conversation'
import type { IUser } from '~/entities/user/user/interfaces/user'

import type { SUBJECT_6W_URI } from '~/features-shared/libs/interfaces/entity'
import { RTInterfaceAPI } from '~/features-shared/rtconv/api/rtinterface-api'
import type {
  RTCConversationId,
  RTCInternalEvent,
  RTCMessageId,
  RTCSectionThread,
  RTCUserId,
} from '~/features-shared/rtconv/interfaces/rtconversation'
import type { TxID } from '~/features-shared/rtconv/interfaces/rtinterface'
import type { ITimeline } from '~/features-shared/timeline/interfaces/timeline'

import type { Section, SectionThread } from '~/features-generic/left-panel/interfaces/left-panel'

/**
 * Sends 'newConversation' event to creates a new conversation.
 * @param options - Options.
 * @param options.users - Members of the new conversation. Can use a combination of uuids/logins/emails.
 * @param options.users.uuids - Members uuids.
 * @param options.users.logins - Members logins.
 * @param options.users.emails - Members emails.
 * @param options.title - Title of the conversation.
 * @param options.isBot - If it is an AI conversation.
 * @param options.waitForResponse - If the promise should wait for RTC server response before resolving.
 * @returns A promise resolving with the newly created conversation or TxID.
 */
export function newConversation({ users, title, isBot, waitForResponse }: {
  users: { uuids?: string[], logins?: string[], emails?: string[] }
  title?: string
  isBot?: boolean
  waitForResponse: true
}): Promise<IConversation>
export function newConversation({ users, title }: {
  users: { uuids?: string[], logins?: string[], emails?: string[] }
  title?: string
  isBot?: boolean
  waitForResponse?: false
}): Promise<TxID>
export function newConversation({ users, title, isBot, waitForResponse }: {
  users: { uuids?: string[], logins?: string[], emails?: string[] }
  title?: string
  isBot?: boolean
  waitForResponse?: boolean
}): Promise<IConversation | TxID> {
  return RTInterfaceAPI.send({
    payload: {
      action: 'newConversation',
      data: { uuids: users?.uuids, logins: users?.logins, emails: users?.emails, title, isBot },
    },
    waitForResponse,
    channel: undefined,
    timeout: 5 * 6e4,
  })
}

/**
 * Sends 'newMessage' event to create a new message.
 * @param options - Options.
 * @param options.convId - Id of the conversation.
 * @param options.contents - List of the contents in the message.
 * @param options.mentions - List of mentioned entities.
 * @param options.mentions.logins - Mentioned users logins.
 * @param options.mentions.usergroups - Mentioned users groups.
 * @param options.parentMessageId - Id of the parent message in case of reply.
 * @param options.isBot - If the message was created by the chat bot.
 * @param options.waitForResponse - If the promise should wait for RTC server response before resolving.
 * @returns A promise resolving with the newly published message or TxID.
 */
export function newMessage({ convId, contents, mentions, parentMessageId, isBot, waitForResponse }: {
  convId: RTCConversationId
  contents: ({ type: 'text', data: string, notifData?: string } | { type: 'textContent', data: string } | { type: 'internal', data: RTCInternalEvent })[]
  mentions?: { 'logins'?: string[], 'usersgroups'?: string[], 'data-audience'?: 'all' }
  parentMessageId?: RTCMessageId
  isBot?: boolean
  waitForResponse: true
}): Promise<ITimeline>
export function newMessage({ convId, contents, mentions, parentMessageId, isBot, waitForResponse }: {
  convId: RTCConversationId
  contents: ({ type: 'text', data: string, notifData?: string } | { type: 'textContent', data: string } | { type: 'internal', data: RTCInternalEvent })[]
  mentions?: { 'logins'?: string[], 'usersgroups'?: string[], 'data-audience'?: 'all' }
  parentMessageId?: RTCMessageId
  isBot?: boolean
  waitForResponse?: false
}): Promise<TxID>
export function newMessage({ convId, contents, mentions, parentMessageId, isBot, waitForResponse }: {
  convId: RTCConversationId
  contents: ({ type: 'text', data: string, notifData?: string } | { type: 'textContent', data: string } | { type: 'internal', data: RTCInternalEvent })[]
  mentions?: { 'logins'?: string[], 'usersgroups'?: string[], 'data-audience'?: 'all' }
  parentMessageId?: RTCMessageId
  isBot?: boolean
  waitForResponse?: boolean
}): Promise<ITimeline | TxID> {
  return RTInterfaceAPI.send({
    payload: {
      action: 'newMessage',
      data: { convId, contents, parentMessageId, mentions, isBot },
    },
    waitForResponse,
  })
}

/**
 * Sends 'forwardMessage' event to forward a message.
 * @param options - Options.
 * @param options.convId - Id of the conversation to forward to.
 * @param options.msgId - Id of the message to forward.
 * @param options.msgConvId - Id of the conversation the message to forward is from.
 * @param options.forwardedFrom - Login of the original author of the message.
 * @param options.waitForResponse - If the promise should wait for RTC server response before resolving.
 * @returns A promise resolving with the forwarded message or TxID.
 */
export function forwardMessage({ convId, msgId, msgConvId, forwardedFrom, mentions, waitForResponse }: {
  convId: RTCConversationId
  msgId: RTCMessageId | SUBJECT_6W_URI
  msgConvId: RTCMessageId | SUBJECT_6W_URI
  forwardedFrom: string
  mentions?: { 'logins'?: string[], 'usersgroups'?: string[], 'data-audience'?: 'all' }
  waitForResponse: true
}): Promise<ITimeline>
export function forwardMessage({ convId, msgId, msgConvId, forwardedFrom, mentions, waitForResponse }: {
  convId: RTCConversationId
  msgId: RTCMessageId | SUBJECT_6W_URI
  msgConvId: RTCMessageId | SUBJECT_6W_URI
  forwardedFrom: string
  mentions?: { 'logins'?: string[], 'usersgroups'?: string[], 'data-audience'?: 'all' }
  waitForResponse?: false
}): Promise<TxID>
export function forwardMessage({ convId, msgId, msgConvId, forwardedFrom, mentions, waitForResponse }: {
  convId: RTCConversationId
  msgId: RTCMessageId | SUBJECT_6W_URI
  msgConvId: RTCMessageId | SUBJECT_6W_URI
  forwardedFrom: string
  mentions?: { 'logins'?: string[], 'usersgroups'?: string[], 'data-audience'?: 'all' }
  waitForResponse?: boolean
}): Promise<ITimeline | TxID> {
  return RTInterfaceAPI.send({
    payload: {
      action: 'newMessage',
      data: { convId, msgId, msgConvId, forwardedFrom, mentions },
    },
    waitForResponse,
  })
}

interface RTCConversationData {
  conversations: IConversation[]
  sections?: Section<IConversation>[]
}
/**
 * Sends 'getConversations' event to get conversations.
 * @param options - Options.
 * @param options.convIds - Ids of the conversations to retrieve. If unspecified, retrieve all.
 * @param options.waitForResponse - If the promise should wait for RTC server response before resolving.
 * @returns A promise resolving with the retrieved conversations or TxID.
 */
export function getConversations({ convIds, isBot, waitForResponse, sections }: {
  convIds?: RTCConversationId[]
  isBot?: boolean
  waitForResponse: true
  sections?: boolean
}): Promise<RTCConversationData>
export function getConversations({ convIds, isBot, waitForResponse, sections }: {
  convIds?: RTCConversationId[]
  isBot?: boolean
  waitForResponse?: false
  sections?: boolean
}): Promise<TxID>
export function getConversations({ convIds, isBot, waitForResponse, sections }: {
  convIds?: RTCConversationId[]
  isBot?: boolean
  waitForResponse?: boolean
  sections?: boolean
}): Promise<RTCConversationData | TxID> {
  return RTInterfaceAPI.send({
    payload: {
      action: 'getConversations',
      data: {
        convIds,
        all: convIds?.length ?? true,
        isBot,
        sections,
      },
    },
    waitForResponse,
  })
}

/**
 * Sends 'getMessages' event to get messages.
 * @param options - Options.
 * @param options.convId - Id of the conversation to retrieve the messages from.
 * @param options.before - If specified, retrieve only messages published before.
 * @param options.around - If specified, retrieve only messages around the given message.
 * @param options.text - If specified, retrieve only messages including the given text in message or author.
 * @param options.contents - If specified, retrieve only messages including the given contents.
 * @param options.waitForResponse - If the promise should wait for RTC server response before resolving.
 * @returns A promise resolving with the retrieved messages or TxID.
 */
export function getMessages({ convId, before, around, text, contents, waitForResponse }: {
  convId: RTCConversationId
  before?: Date
  around?: RTCMessageId
  text?: string
  contents?: Array<SUBJECT_6W_URI>
  waitForResponse: true
}): Promise<ITimeline[]>
export function getMessages({ convId, before, around, text, contents, waitForResponse }: {
  convId: RTCConversationId
  before?: Date
  around?: RTCMessageId
  text?: string
  contents?: Array<SUBJECT_6W_URI>
  waitForResponse?: false
}): Promise<TxID>
export function getMessages({ convId, before, around, text, contents, waitForResponse }: {
  convId: RTCConversationId
  before?: Date
  around?: RTCMessageId
  text?: string
  contents?: Array<SUBJECT_6W_URI>
  waitForResponse?: boolean
}): Promise<ITimeline[] | TxID> {
  return RTInterfaceAPI.send({
    payload: {
      action: 'getMessages',
      data: { convId, before, around, text, contents },
    },
    waitForResponse,
  })
}

/**
 * Sends 'newConversationUsers' event to add users to a conversation.
 * @param options - Options.
 * @param options.convId - Id of the conversation.
 * @param options.users - Members to add to the conversation. Can use a combination of uuids/logins/emails.
 * @param options.users.uuids - Members uuids.
 * @param options.users.logins - Members logins.
 * @param options.users.emails - Members emails.
 * @param options.waitForResponse - If the promise should wait for RTC server response before resolving.
 * @returns A promise resolving with the list of conversation members or TxID.
 */
export function newConversationUsers({ convId, users, waitForResponse }: {
  convId: RTCConversationId
  users: { uuids?: string[], logins?: string[], emails?: string[] }
  waitForResponse: true
}): Promise<IUser[]>
export function newConversationUsers({ convId, users, waitForResponse }: {
  convId: RTCConversationId
  users: { uuids?: string[], logins?: string[], emails?: string[] }
  waitForResponse?: false
}): Promise<TxID>
export function newConversationUsers({ convId, users, waitForResponse }: {
  convId: RTCConversationId
  users: { uuids?: string[], logins?: string[], emails?: string[] }
  waitForResponse?: boolean
}): Promise<IUser[] | TxID> {
  return RTInterfaceAPI.send({
    payload: {
      action: 'newConversationUsers',
      data: { convId, uuids: users?.uuids, logins: users?.logins, emails: users?.emails },
    },
    waitForResponse,
  })
}

/**
 * Sends 'removeConversationUser' event to remove a user from a conversation.
 * @param options - Options.
 * @param options.convId - Id of the conversation.
 * @param options.userId - Id of the user to remove from the conversation.
 * @param options.waitForResponse - If the promise should wait for RTC server response before resolving.
 * @returns A promise resolving with no data or TxID.
 */
export function removeConversationUser({ convId, userId, waitForResponse }: {
  convId: RTCConversationId
  userId: RTCMessageId
  waitForResponse: true
}): Promise<void>
export function removeConversationUser({ convId, userId, waitForResponse }: {
  convId: RTCConversationId
  userId: RTCMessageId
  waitForResponse?: false
}): Promise<TxID>
export function removeConversationUser({ convId, userId, waitForResponse }: {
  convId: RTCConversationId
  userId: RTCMessageId
  waitForResponse?: boolean
}): Promise<void | TxID> {
  return RTInterfaceAPI.send({
    payload: {
      action: 'removeConversationUser',
      data: { convId, userId },
    },
    waitForResponse,
  })
}

/**
 * Sends 'getConversationsByMembers' event to get conversations that include given members.
 * @param options - Options.
 * @param options.users - Members of the conversations. Can use a combination of uuids/logins/emails.
 * @param options.users.uuids - Members uuids.
 * @param options.users.logins - Members logins.
 * @param options.users.emails - Members emails.
 * @param options.waitForResponse - If the promise should wait for RTC server response before resolving.
 * @returns A promise resolving with no data or TxID.
 */
export function getConversationsByMembers({ users, waitForResponse }: {
  users: { uuids?: string[], logins?: string[], emails?: string[] }
  waitForResponse: true
}): Promise<IConversation[]>
export function getConversationsByMembers({ users, waitForResponse }: {
  users: { uuids?: string[], logins?: string[], emails?: string[] }
  waitForResponse?: false
}): Promise<TxID>
export function getConversationsByMembers({ users, waitForResponse }: {
  users: { uuids?: string[], logins?: string[], emails?: string[] }
  waitForResponse?: boolean
}): Promise<IConversation[] | TxID> {
  return RTInterfaceAPI.send({
    payload: {
      action: 'getConversationsByMembers',
      data: { uuids: users?.uuids, logins: users?.logins, emails: users?.emails },
    },
    waitForResponse,
  })
}

/**
 * Sends 'getConvMembers' event to get the members of a conversation.
 * @param options - Options.
 * @param options.convId - Id of the conversation.
 * @param options.waitForResponse - If the promise should wait for RTC server response before resolving.
 * @returns A promise resolving with no data or TxID.
 */
export function getConvMembers({ convId, waitForResponse }: {
  convId: RTCConversationId
  waitForResponse: true
}): Promise<IUser[]>
export function getConvMembers({ convId, waitForResponse }: {
  convId: RTCConversationId
  waitForResponse?: false
}): Promise<TxID>
export function getConvMembers({ convId, waitForResponse }: {
  convId: RTCConversationId
  waitForResponse?: boolean
}): Promise<IUser[] | TxID> {
  return RTInterfaceAPI.send({
    payload: {
      action: 'getConvMembers',
      data: { convId },
    },
    waitForResponse,
  })
}

/**
 * Sends 'updateConvTitle' event to update the title of a conversation.
 * @param options - Options.
 * @param options.convId - Id of the conversation.
 * @param options.title - New title of the conversation. Reset the title if null.
 * @param options.isBot - If it is an AI conversation.
 * @param options.waitForResponse - If the promise should wait for RTC server response before resolving.
 * @returns A promise resolving with the new title of the conversation or TxID.
 */
export function updateConvTitle({ convId, title, isBot, waitForResponse }: {
  convId: RTCConversationId
  title: string | null
  isBot?: boolean
  waitForResponse: true
}): Promise<string>
export function updateConvTitle({ convId, title, isBot, waitForResponse }: {
  convId: RTCConversationId
  title: string | null
  isBot?: boolean
  waitForResponse?: false
}): Promise<TxID>
export function updateConvTitle({ convId, title, isBot, waitForResponse }: {
  convId: RTCConversationId
  title: string | null
  isBot?: boolean
  waitForResponse?: boolean
}): Promise<void | TxID> {
  return RTInterfaceAPI.send({
    payload: {
      action: 'updateConvTitle',
      data: { convId, title, isBot },
    },
    waitForResponse,
  })
}

/**
 * Sends 'setConversationHidden' event to hide/unhide a conversation.
 * @param options - Options.
 * @param options.convId - Id of the conversation.
 * @param options.isHidden - If the conversation should be hidden or unhidden.
 * @param options.waitForResponse - If the promise should wait for RTC server response before resolving.
 * @returns A promise resolving with the conversation visibility state or TxID.
 */
export function setConversationHidden({ convId, isHidden, waitForResponse }: {
  convId: RTCConversationId
  isHidden: boolean
  waitForResponse: true
}): Promise<boolean>
export function setConversationHidden({ convId, isHidden, waitForResponse }: {
  convId: RTCConversationId
  isHidden: boolean
  waitForResponse?: false
}): Promise<TxID>
export function setConversationHidden({ convId, isHidden, waitForResponse }: {
  convId: RTCConversationId
  isHidden: boolean
  waitForResponse?: boolean
}): Promise<boolean | TxID> {
  return RTInterfaceAPI.send({
    payload: {
      action: 'setConversationHidden',
      data: { convId, isHidden },
    },
    waitForResponse,
  })
}

/**
 * Sends 'setConversationFavorite' event to set/unset a favorite conversation.
 * @param options - Options.
 * @param options.convId - Id of the conversation.
 * @param options.favOrder - Position of the conversation in the favorite list.
 * @param options.waitForResponse - If the promise should wait for RTC server response before resolving.
 * @returns A promise resolving with no data or TxID.
 */
export function setConversationFavorite({ convId, favOrder, waitForResponse }: {
  convId: RTCConversationId
  favOrder: number
  waitForResponse: true
}): Promise<RTCSectionThread[]>
export function setConversationFavorite({ convId, favOrder, waitForResponse }: {
  convId: RTCConversationId
  favOrder: number
  waitForResponse?: false
}): Promise<TxID>
export function setConversationFavorite({ convId, favOrder, waitForResponse }: {
  convId: RTCConversationId
  favOrder: number
  waitForResponse?: boolean
}): Promise<RTCSectionThread[] | TxID> {
  return RTInterfaceAPI.send({
    payload: {
      action: 'setConversationFavorite',
      data: { convId, favOrder },
    },
    waitForResponse,
  })
}

/**
 * Sends 'readConversation' event to set the last message of a conversation.
 * @param options - Options.
 * @param options.convId - Id of the conversation.
 * @param options.msgId - Id of the last message read.
 * @param options.waitForResponse - If the promise should wait for RTC server response before resolving.
 * @returns A promise resolving with the last message read or TxID.
 */
export function readConversation({ convId, msgId, waitForResponse }: {
  convId: RTCConversationId
  msgId: RTCMessageId
  waitForResponse: true
}): Promise<void>
export function readConversation({ convId, msgId, waitForResponse }: {
  convId: RTCConversationId
  msgId: RTCMessageId
  waitForResponse?: false
}): Promise<TxID>
export function readConversation({ convId, msgId, waitForResponse }: {
  convId: RTCConversationId
  msgId: RTCMessageId
  waitForResponse?: boolean
}): Promise<void | TxID> {
  return RTInterfaceAPI.send({
    payload: {
      action: 'readConversation',
      data: { convId, msgId },
    },
    waitForResponse,
  })
}

/**
 * Sends 'autoRemoval' event to activate/desactivate the conversation auto removal.
 * @param options - Options.
 * @param options.convId - Id of the conversation.
 * @param options.autoRemovalEnabled - If auto removal of the conversation is enabled.
 * @param options.waitForResponse - If the promise should wait for RTC server response before resolving.
 * @returns A promise resolving with the conversation persistency state state or TxID.
 */
export function autoRemoval({ convId, autoRemovalEnabled, waitForResponse }: {
  convId: RTCConversationId
  autoRemovalEnabled: boolean
  waitForResponse: true
}): Promise<boolean>
export function autoRemoval({ convId, autoRemovalEnabled, waitForResponse }: {
  convId: RTCConversationId
  autoRemovalEnabled: boolean
  waitForResponse?: false
}): Promise<TxID>
export function autoRemoval({ convId, autoRemovalEnabled, waitForResponse }: {
  convId: RTCConversationId
  autoRemovalEnabled: boolean
  waitForResponse?: boolean
}): Promise<boolean | TxID> {
  return RTInterfaceAPI.send({
    payload: {
      action: 'autoRemoval',
      data: { convId, autoRemovalEnabled },
    },
    waitForResponse,
  })
}

/**
 * Sends 'editMessage' event to edit the text of a message.
 * @param options - Options.
 * @param options.convId - Id of the conversation.
 * @param options.msgId - Id of the message.
 * @param options.text - New text of the message.
 * @param options.waitForResponse - If the promise should wait for RTC server response before resolving.
 * @returns A promise resolving with no data or TxID.
 */
export function editMessage({ convId, msgId, text, waitForResponse }: {
  convId: RTCConversationId
  msgId: RTCMessageId
  text: string
  waitForResponse: true
}): Promise<void>
export function editMessage({ convId, msgId, text, waitForResponse }: {
  convId: RTCConversationId
  msgId: RTCMessageId
  text: string
  waitForResponse?: false
}): Promise<TxID>
export function editMessage({ convId, msgId, text, waitForResponse }: {
  convId: RTCConversationId
  msgId: RTCMessageId
  text: string
  waitForResponse?: boolean
}): Promise<void | TxID> {
  return RTInterfaceAPI.send({
    payload: {
      action: 'editMessage',
      data: {
        convId,
        message: {
          id: msgId,
          contents: [{ id: msgId, type: 'text', data: text }],
        },
      },
    },
    waitForResponse,
  })
}

/**
 * Sends 'deleteMessage' event to delete a message.
 * @param options - Options.
 * @param options.convId - Id of the conversation.
 * @param options.msgId - Id of the message.
 * @param options.waitForResponse - If the promise should wait for RTC server response before resolving.
 * @returns A promise resolving with no data or TxID.
 */
export function deleteMessage({ convId, msgId, waitForResponse }: {
  convId: RTCConversationId
  msgId: RTCMessageId
  waitForResponse: true
}): Promise<void>
export function deleteMessage({ convId, msgId, waitForResponse }: {
  convId: RTCConversationId
  msgId: RTCMessageId
  waitForResponse?: false
}): Promise<TxID>
export function deleteMessage({ convId, msgId, waitForResponse }: {
  convId: RTCConversationId
  msgId: RTCMessageId
  waitForResponse?: boolean
}): Promise<void | TxID> {
  return RTInterfaceAPI.send({
    payload: {
      action: 'deleteMessage',
      data: { convId, msgId },
    },
    waitForResponse,
  })
}

/**
 * Sends 'likeMessage' event to like/dislike a message.
 * @param options - Options.
 * @param options.convId - Id of the conversation.
 * @param options.msgId - Id of the message.
 * @param options.liked - If the message was liked or disliked.
 * @param options.waitForResponse - If the promise should wait for RTC server response before resolving.
 * @returns A promise resolving with the current user id or TxID.
 */
export function likeMessage({ convId, msgId, liked, waitForResponse }: {
  convId: RTCConversationId
  msgId: RTCMessageId
  liked: boolean
  waitForResponse: true
}): Promise<string>
export function likeMessage({ convId, msgId, liked, waitForResponse }: {
  convId: RTCConversationId
  msgId: RTCMessageId
  liked: boolean
  waitForResponse?: false
}): Promise<TxID>
export function likeMessage({ convId, msgId, liked, waitForResponse }: {
  convId: RTCConversationId
  msgId: RTCMessageId
  liked: boolean
  waitForResponse?: boolean
}): Promise<string | TxID> {
  return RTInterfaceAPI.send({
    payload: {
      action: 'likeMessage',
      data: { convId, msgId, liked },
    },
    waitForResponse,
  })
}

/**
 * Sends 'getLikes' event to get the likes of a message.
 * @param options - Options.
 * @param options.convId - Id of the conversation.
 * @param options.msgId - Id of the message.
 * @param options.waitForResponse - If the promise should wait for RTC server response before resolving.
 * @returns A promise resolving with the list of users who endorse the message or TxID.
 */
export function getLikes({ convId, msgId, waitForResponse }: {
  convId: RTCConversationId
  msgId: RTCMessageId
  waitForResponse: true
}): Promise<IUser[]>
export function getLikes({ convId, msgId, waitForResponse }: {
  convId: RTCConversationId
  msgId: RTCMessageId
  waitForResponse?: false
}): Promise<TxID>
export function getLikes({ convId, msgId, waitForResponse }: {
  convId: RTCConversationId
  msgId: RTCMessageId
  waitForResponse?: boolean
}): Promise<IUser[] | TxID> {
  return RTInterfaceAPI.send({
    payload: {
      action: 'getLikes',
      data: { convId, msgId },
    },
    waitForResponse,
  })
}

/**
 * Sends 'setConversationSubscribed' event to subscribe/unsubcribe to new content published on conversation.
 * @param options - Options.
 * @param options.convId - Id of the conversation.
 * @param options.isSubscribed - If the user is subcribring or subscribing to the conversation.
 * @param options.subjectUri - Subject uri of the conversation.
 * @param options.waitForResponse - If the promise should wait for RTC server response before resolving.
 * @returns A promise resolving with no data or TxID.
 */
export function setConversationSubscribed({ convId, isSubscribed, subjectUri, waitForResponse }: {
  convId: RTCConversationId
  isSubscribed: boolean
  subjectUri: SUBJECT_6W_URI
  waitForResponse: true
}): Promise<boolean>
export function setConversationSubscribed({ convId, isSubscribed, subjectUri, waitForResponse }: {
  convId: RTCConversationId
  isSubscribed: boolean
  subjectUri: SUBJECT_6W_URI
  waitForResponse?: false
}): Promise<TxID>
export function setConversationSubscribed({ convId, isSubscribed, subjectUri, waitForResponse }: {
  convId: RTCConversationId
  isSubscribed: boolean
  subjectUri: SUBJECT_6W_URI
  waitForResponse?: boolean
}): Promise<boolean | TxID> {
  return RTInterfaceAPI.send({
    payload: {
      action: 'setConversationSubscribed',
      data: { convId, isSubscribed, subjectUri },
    },
    waitForResponse,
  })
}

/**
 * Sends 'setConversationMuted' event to mute/unmute conversation.
 * @param options - Options.
 * @param options.convId - Id of the conversation.
 * @param options.isMuted - If the user mute or unmute to the conversation.
 * @param options.subjectUri - Subject uri of the conversation.
 * @param options.waitForResponse - If the promise should wait for RTC server response before resolving.
 * @returns A promise resolving with mute/unmute state or TxID.
 */
export function setConversationMuted({ convId, isMuted, subjectUri, waitForResponse }: {
  convId: RTCConversationId
  isMuted?: boolean
  subjectUri: SUBJECT_6W_URI
  waitForResponse: true
}): Promise<boolean>
export function setConversationMuted({ convId, isMuted, subjectUri, waitForResponse }: {
  convId: RTCConversationId
  isMuted?: boolean
  subjectUri: SUBJECT_6W_URI
  waitForResponse: false
}): Promise<boolean>
export function setConversationMuted({ convId, isMuted, subjectUri, waitForResponse }: {
  convId: RTCConversationId
  isMuted?: boolean
  subjectUri: SUBJECT_6W_URI
  waitForResponse?: boolean
}): Promise<boolean | TxID> {
  return RTInterfaceAPI.send({
    payload: {
      action: 'setConversationMuted',
      data: { convId, isMuted, subjectUri },
    },
    waitForResponse,
  })
}

/**
 * Sends 'resetMissedBadge' event to reset the count of missed calls on 'Calls' badge.
 * @param options - Options.
 * @param options.waitForResponse - If the promise should wait for RTC server response before resolving.
 * @returns A promise resolving with the number of missed calls or TxID.
 */
export function resetMissedBadge({ waitForResponse }: { waitForResponse: true }): Promise<number>
export function resetMissedBadge({ waitForResponse }: { waitForResponse?: false }): Promise<TxID>
export function resetMissedBadge({ waitForResponse }: { waitForResponse?: boolean }): Promise<number | TxID> {
  return RTInterfaceAPI.send({
    payload: {
      action: 'missedBadge',
      data: { count: 0 },
    },
    waitForResponse,
    channel: 'callToRTC.im.ds.com',
  })
}

/**
 * Sends 'getUser' event to get a user.
 * @param options - Options.
 * @param options.waitForResponse - If the promise should wait for RTC server response before resolving.
 * @returns A promise resolving with the user info or TxID.
 */
export function getUser({ userId, uuid, login, email, phone, waitForResponse }: {
  userId?: RTCUserId
  uuid?: string
  login?: string
  email?: string
  phone?: string
  waitForResponse: true
}): Promise<IUser>
export function getUser({ userId, uuid, login, email, phone, waitForResponse }: {
  userId?: RTCUserId
  uuid?: string
  login?: string
  email?: string
  phone?: string
  waitForResponse?: false
}): Promise<TxID>
export function getUser({ userId, uuid, login, email, phone, waitForResponse }: {
  userId?: RTCUserId
  uuid?: string
  login?: string
  email?: string
  phone?: string
  waitForResponse?: boolean
}): Promise<IUser | TxID> {
  return RTInterfaceAPI.send({
    payload: {
      action: 'getUser',
      data: { userId, uuid, login, email, phone },
    },
    waitForResponse,
  })
}

/**
 * Sends 'createSection' event to create section.
 * @param options -
 * @param options.name name of section
 * @param options.position position of section
 * @param options.waitForResponse - If the promise should wait for RTC server response before resolving.
 * @returns A promise resolving with section data  or TxID.
 */
export function createSection({ name, position, waitForResponse }: {
  name: string
  position: number
  waitForResponse: true
}): Promise<TxID>
export function createSection({ name, position, waitForResponse }: {
  name: string
  position: number
  waitForResponse?: false
}): Promise<Section<IConversation>[]>
export function createSection({ name, position, waitForResponse }: {
  name: string
  position: number
  waitForResponse?: boolean
}): Promise<TxID | Section<IConversation>[]> {
  return RTInterfaceAPI.send({
    payload: {
      action: 'createSection',
      data: { name, position },
    },
    waitForResponse,
  })
}

/**
 * Sends 'deleteSection' event to delete Section.
 * @param options -
 * @param options.sectionId Id of section
 * @param options.waitForResponse - If the promise should wait for RTC server response before resolving.
 * @returns A promise resolving with delete section or TxID.
 */
export function deleteSection({ sectionId, waitForResponse }: {
  sectionId: number
  waitForResponse: true
}): Promise<TxID>
export function deleteSection({ sectionId, waitForResponse }: {
  sectionId: number
  waitForResponse?: false
}): Promise<number>
export function deleteSection({ sectionId, waitForResponse }: {
  sectionId: number
  waitForResponse?: boolean
}): Promise<TxID | number> {
  return RTInterfaceAPI.send({
    payload: {
      action: 'deleteSection',
      data: { sectionId },
    },
    waitForResponse,
  })
}

/**
 * Sends 'renameSection' event to rename section.
 * @param options -
 * @param options.name  New name of section
 * @param options.id Id of section
 * @param options.waitForResponse - If the promise should wait for RTC server response before resolving.
 * @returns A promise resolving with rename section list or TxID.
 */
export function renameSection({ name, id, waitForResponse }: {
  name: string
  id: number
  waitForResponse: true
}): Promise<TxID>
export function renameSection({ name, id, waitForResponse }: {
  name: string
  id: number
  waitForResponse?: false
}): Promise<Section<IConversation>[]>
export function renameSection({ name, id, waitForResponse }: {
  name: string
  id: number
  waitForResponse?: boolean
}): Promise<TxID | Section<IConversation>[]> {
  return RTInterfaceAPI.send({
    payload: {
      action: 'renameSection',
      data: { name, id },
    },
    waitForResponse,
  })
}

/**
 * Sends 'addToSection' event to add conversation to section.
 * @param options -
 * @param options.convId Id of the conversation to add in the section
 * @param options.sectionId Id of the section the conversation will be added to
 * @param options.position 	Position where to insert the conversation in the section
 * @param options.waitForResponse - If the promise should wait for RTC server response before resolving.
 * @returns A promise resolving with add conversation to section or TxID.
 */
export function addToSection({ convId, sectionId, position, waitForResponse }: {
  convId: number
  sectionId: number
  position: number
  waitForResponse: true
}): Promise<TxID>
export function addToSection({ convId, sectionId, position, waitForResponse }: {
  convId: number
  sectionId: number
  position: number
  waitForResponse?: false
}): Promise<Section<IConversation>[]>
export function addToSection({ convId, sectionId, position, waitForResponse }: {
  convId: number
  sectionId: number
  position: number
  waitForResponse?: boolean
}): Promise<TxID | Section<IConversation>[]> {
  return RTInterfaceAPI.send({
    payload: {
      action: 'addToSection',
      data: { convId, sectionId, position },
    },
    waitForResponse,
  })
}

/**
 * Sends 'getSections' event to get section list.
 * @param options.waitForResponse - If the promise should wait for RTC server response before resolving.
 * @returns A promise resolving with section list or TxID.
 */
export function getSections({ waitForResponse }: {
  waitForResponse: true
}): Promise<TxID>
export function getSections({ waitForResponse }: {
  waitForResponse?: false
}): Promise<Section<IConversation>[]>
export function getSections({ waitForResponse }: {
  waitForResponse?: boolean
}): Promise<TxID | Section<IConversation>[]> {
  return RTInterfaceAPI.send({
    payload: {
      action: 'getSections',
    },
    waitForResponse,
  })
}

/**
 * Sends 'removeFromSection' event to remove conversation in section.
 * @param options -
 * @param options.convId Id of the conversation
 * @param options.sectionId Id of the section
 * @param options.waitForResponse - If the promise should wait for RTC server response before resolving.
 * @returns A promise resolving with The new list of conversations in the section after deletion or TxID.
 */
export function removeFromSection({ convId, sectionId, waitForResponse }: {
  convId: number
  sectionId: number
  waitForResponse: true
}): Promise<TxID>
export function removeFromSection({ convId, sectionId, waitForResponse }: {
  convId: number
  sectionId: number
  waitForResponse?: false
}): Promise<SectionThread[]>
export function removeFromSection({ convId, sectionId, waitForResponse }: {
  convId: number
  sectionId: number
  waitForResponse?: boolean
}): Promise<TxID | SectionThread[]> {
  return RTInterfaceAPI.send({
    payload: {
      action: 'removeFromSection',
      data: { convId, sectionId },
    },
    waitForResponse,
  })
}

/**
 * Sends 'reorderSection' event to reorder section list.
 * @param options -
 * @param options.sectionId Id of the section
 * @param options.position New position for the section in the sections list
 * @param options.waitForResponse - If the promise should wait for RTC server response before resolving.
 * @returns A promise resolving with The list of sections of the user after reorderings or TxID.
 */
export function reorderSection({ sectionId, position, waitForResponse }: {
  sectionId: number
  position: number
  waitForResponse: true
}): Promise<TxID>
export function reorderSection({ sectionId, position, waitForResponse }: {
  sectionId: number
  position: number
  waitForResponse?: false
}): Promise<Section<IConversation>[]>
export function reorderSection({ sectionId, position, waitForResponse }: {
  sectionId: number
  position: number
  waitForResponse?: boolean
}): Promise<TxID | Section<IConversation>[]> {
  return RTInterfaceAPI.send({
    payload: {
      action: 'reorderSection',
      data: { sectionId, position },
    },
    waitForResponse,
  })
}

/**
 * Sends 'reorderInSection' event to reorder section list.
 * @param options -
 * @param options.sectionId Id of the section
 * @param options.position New position for the section in the sections list
 * @param options.waitForResponse - If the promise should wait for RTC server response before resolving.
 * @returns A promise resolving with The list of sections of the user after reorderings or TxID.
 */
export function reorderInSection({ convId, sectionId, position, waitForResponse }: {
  convId: number
  sectionId: number
  position: number
  waitForResponse: true
}): Promise<RTCSectionThread[]>
export function reorderInSection({ convId, sectionId, position, waitForResponse }: {
  convId: number
  sectionId: number
  position: number
  waitForResponse?: false
}): Promise<TxID>
export function reorderInSection({ convId, sectionId, position, waitForResponse }: {
  convId: number
  sectionId: number
  position: number
  waitForResponse?: boolean
}): Promise<TxID | RTCSectionThread[]> {
  return RTInterfaceAPI.send({
    payload: {
      action: 'reorderInSection',
      data: { convId, sectionId, position },
    },
    waitForResponse,
  })
}
